import { useState, useEffect } from "react";
import { getApiUrl } from "./config";

export interface PlanFeature {
  pt: string;
  en: string;
  included: boolean;
}

export interface PlanData {
  id: string;
  planType: string;
  name: string;
  descriptionPt: string;
  descriptionEn: string;
  documentLimit: number;
  userLimit: number;
  priceMonthlyBrl: number;
  priceMonthlyUsd: number;
  priceAnnualBrl: number;
  priceAnnualUsd: number;
  contactSales: boolean;
  features: string;
  icon: string;
  colorGradient: string;
  popular: boolean;
  includesSso: boolean;
  includesDynamics: boolean;
  includesApi: boolean;
  includesBranding: boolean;
}

/**
 * Fetches active plans from the public API endpoint.
 * Returns null if API is unavailable (use hardcoded fallback).
 */
export function usePlans() {
  const [plans, setPlans] = useState<PlanData[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await fetch(getApiUrl("/api/v1/plans"));
        if (!res.ok) throw new Error("API error");
        const data = await res.json();
        if (!cancelled) setPlans(data);
      } catch {
        // Intentional, non-sensitive diagnostic: prices fell back to the built-in defaults.
        console.error("[FillSign] Could not load plans from the API — showing default plan values.");
        if (!cancelled) setPlans(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  return { plans, loading };
}

export function parseFeatures(featuresJson: string): PlanFeature[] {
  try { return JSON.parse(featuresJson); }
  catch { return []; }
}
